import React, { useState, useMemo } from 'react';
import { FaLaptop, FaMobileAlt, FaWarehouse, FaSearch, FaPlusCircle, FaRupeeSign } from 'react-icons/fa';
import { Clock, BarChart3 } from 'lucide-react';
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';

// --- Reusable Components ---

// 1. Asset Metric Card
const AssetCard = ({ title, value, icon: Icon, colorClass }) => (
  <div className={`p-5 rounded-xl shadow-md transition duration-300 hover:shadow-lg bg-white border-l-4 ${colorClass}`}>
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      <Icon className="text-2xl text-gray-500" />
    </div>
    <div className="mt-2 text-3xl font-bold text-gray-900">
      {value}
    </div>
  </div>
);

// 2. Asset Checkout/Checkin Form
const AssetAssignmentForm = ({ onSubmit }) => {
  const [assetId, setAssetId] = useState('');
  const [employee, setEmployee] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (assetId && employee) {
      onSubmit({ assetId, employee });
      setAssetId('');
      setEmployee('');
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <FaPlusCircle className="mr-2 text-green-600" /> Asset Checkout/Checkin
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="asset" className="block text-sm font-medium text-gray-700">Asset ID / Name</label>
          <input
            type="text"
            id="asset"
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            placeholder="e.g., LAP-DEL-005"
            className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label htmlFor="employee" className="block text-sm font-medium text-gray-700">Assign to Employee (Name/ID)</label>
          <input
            type="text"
            id="employee"
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
            placeholder="e.g., Ramesh Kumar (EMP123)"
            className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <button
          type="submit"
          className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-150 ease-in-out"
        >
          Assign Asset
        </button>
      </form>
    </div>
  );
};

// --- Dummy Data (Indian Context) ---
const mockAssets = [
  { id: 'LAP-DEL-005', type: 'Laptop', name: 'Ramesh Kumar', status: 'Assigned', purchasePrice: 75000, assignedDate: '2024-05-10' },
  { id: 'LAP-HP-012', type: 'Laptop', name: null, status: 'Available', purchasePrice: 65000, assignedDate: null },
  { id: 'MOB-SAM-021', type: 'Mobile Phone', name: 'Priya Sharma', status: 'Assigned', purchasePrice: 22000, assignedDate: '2025-01-20' },
  { id: 'MON-LG-003', type: 'Monitor', name: 'Arjun Reddy', status: 'Assigned', purchasePrice: 15000, assignedDate: '2024-06-01' },
];

const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

// --- Main Dashboard Component ---

const AssetManagement = () => {
  const [assets, setAssets] = useState(mockAssets);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Asset-specific activity types
  const assetActivityTypes = [
    "Asset Assigned", "Asset Returned", "Asset Created", 
    "Asset Updated", "Asset Maintenance", "Asset Disposed"
  ];

  // Asset-specific report types
  const assetReportTypes = [
    "Asset Allocation Report", "Asset Utilization Report", 
    "Asset Maintenance Report", "Asset Depreciation Report",
    "Asset Audit Report", "Asset Cost Analysis"
  ];

  // Calculate KPIs
  const totalValue = assets.reduce((sum, asset) => sum + asset.purchasePrice, 0);
  const assignedCount = assets.filter(a => a.status === 'Assigned').length;
  const availableCount = assets.filter(a => a.status === 'Available').length;

  const handleAssignment = (data) => {
    // In a real app, this would call an API to update the status.
    console.log('Assigning Asset:', data);
    alert(`Asset ${data.assetId} successfully assigned to ${data.employee}!`);
    // Mock state update
    setAssets(prev => prev.map(a => 
      a.id === data.assetId ? { ...a, name: data.employee, status: 'Assigned', assignedDate: new Date().toISOString().slice(0, 10) } : a
    ));
  };

  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      const matchesSearch = (asset.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (asset.name && asset.name.toLowerCase().includes(searchTerm.toLowerCase())));
      const matchesStatus = statusFilter === 'All' || asset.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [assets, searchTerm, statusFilter]);

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold text-gray-800 border-b pb-2">Asset Management Dashboard 💻</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Clock size={16} /> Activity Logs
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'reports' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <BarChart3 size={16} /> Reports
          </button>
        </div>
      </div>
      
      {activeTab === 'activity' && (
        <ActivityLogs moduleName="Asset Management" activityTypes={assetActivityTypes} />
      )}
      
      {activeTab === 'reports' && (
        <Reports moduleName="Asset Management" reportTypes={assetReportTypes} />
      )}
      
      {activeTab === 'dashboard' && (
        <>
      {/* 1. KPI Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <AssetCard
          title="Total Asset Value (Purchase)"
          value={formatCurrency(totalValue)}
          icon={FaRupeeSign}
          colorClass="border-blue-500"
        />
        <AssetCard
          title="Total Assets Assigned"
          value={assignedCount}
          icon={FaLaptop}
          colorClass="border-yellow-500"
        />
        <AssetCard
          title="Available Inventory"
          value={availableCount}
          icon={FaWarehouse}
          colorClass="border-green-500"
        />
        <AssetCard
          title="High-Value Assets (>₹50k)"
          value={assets.filter(a => a.purchasePrice > 50000).length}
          icon={FaMobileAlt}
          colorClass="border-red-500"
        />
      </div>

      {/* 2. Assignment Form and Inventory List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        
        {/* Assignment Form (Left Column - Smaller) */}
        <div className="lg:col-span-1">
          <AssetAssignmentForm onSubmit={handleAssignment} />
        </div>
        
        {/* Asset List (Right Column - Larger) */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Current Asset Inventory</h2>
          
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by ID or Employee Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
            >
              <option value="All">All Statuses</option>
              <option value="Assigned">Assigned</option>
              <option value="Available">Available</option>
            </select>
          </div>

          {/* Asset Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Custodian</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssets.map((asset, index) => (
                  <tr key={index} className="hover:bg-blue-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{asset.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{asset.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-indigo-700">{asset.name || 'Inventory Stock'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        asset.status === 'Assigned' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {asset.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{asset.assignedDate || '-'}</td>
                  </tr>
                ))}
                {filteredAssets.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-500">No assets found matching your criteria.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default AssetManagement;